import { useState } from 'react'
import { ChevronDown, ChevronUp, Loader2 } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  ZanshinPair,
  PENDING_AGE_AMBER_S,
  PENDING_AGE_RED_S,
} from '@/services/zanshin.service'
import { getStateMeta } from './stateMeta'

function fmtPrice(v: number | null): string {
  return v === null || v === undefined ? '—' : v.toFixed(2)
}

interface ZanshinPairCardProps {
  pair: ZanshinPair
  showUserId?: boolean
}

export function ZanshinPairCard({ pair, showUserId = false }: ZanshinPairCardProps) {
  const [expanded, setExpanded] = useState(false)
  const op = pair.operation
  const meta = getStateMeta(op?.zanshin_state ?? null, op?.shield_phase ?? 0)

  // Guadagno protetto (Q2 opzione A): solo se positivo.
  // (broker_sl_current - broker_entry_price) * broker_lots * 100
  // 100 = pip_value approssimato per XAUUSD lot standard.
  let protectedGain: number | null = null
  if (op && op.broker_sl_current && op.broker_entry_price && op.broker_lots) {
    const dir = op.prop_direction === 'BUY' ? -1 : 1  // broker e' opposto al prop
    const raw = (op.broker_sl_current - op.broker_entry_price) * op.broker_lots * 100 * dir
    if (raw > 0) protectedGain = raw
  }

  // Pending age color
  const pendingAge = op?.pending_age_s ?? null
  const pendingColor =
    pendingAge === null ? null :
    pendingAge > PENDING_AGE_RED_S   ? 'red'   :
    pendingAge > PENDING_AGE_AMBER_S ? 'amber' : 'amber'

  const dimmed = op === null

  return (
    <Card className={`${meta.bg} ${meta.border} border ${dimmed ? 'opacity-60' : ''} p-4 transition-all`}>
      {/* ── Header coppia ──────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="font-semibold text-foreground truncate">{pair.display_name}</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {pair.prop_provider_label} · {pair.prop_phase_label}
            {showUserId && pair.user_id != null && (
              <span className="ml-2 px-1.5 py-0.5 rounded bg-[#1f2937] text-[#9ca3af] text-[10px]">
                user #{pair.user_id}
              </span>
            )}
          </div>
        </div>
        {/* EA status pallini */}
        <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground shrink-0">
          <span title={`PROP EA ${pair.prop_ea_connected ? 'connesso' : 'disconnesso'}`}
            className={`inline-block w-2 h-2 rounded-full ${pair.prop_ea_connected ? 'bg-green-500' : 'bg-gray-600'}`} />
          <span>P</span>
          <span title={`BROKER EA ${pair.broker_ea_connected ? 'connesso' : 'disconnesso'}`}
            className={`inline-block w-2 h-2 rounded-full ml-1 ${pair.broker_ea_connected ? 'bg-green-500' : 'bg-gray-600'}`} />
          <span>B</span>
        </div>
      </div>

      {/* ── Badge stato principale ─────────────────────────────────────── */}
      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md ${meta.text} font-medium text-sm`}>
        <meta.Icon width={18} height={18} />
        {meta.label}
      </div>

      {/* ── Pending overlay (se applicabile) ───────────────────────────── */}
      {op?.is_pending && pendingAge !== null && (
        <div className={`mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium
          ${pendingColor === 'red'
            ? 'bg-red-950 text-red-300 border border-red-800'
            : 'bg-amber-950 text-amber-300 border border-amber-800'}`}>
          <Loader2 className="w-3 h-3 animate-spin" />
          EA in conferma · {pendingAge.toFixed(0)}s
        </div>
      )}

      {/* ── Guadagno protetto (solo se positivo) ───────────────────────── */}
      {protectedGain !== null && (
        <div className="mt-3 text-sm">
          <span className="text-muted-foreground">Guadagno protetto: </span>
          <span className="text-green-400 font-semibold">€{protectedGain.toFixed(0)}</span>
        </div>
      )}

      {/* ── Dettagli tecnici (espandibili) ─────────────────────────────── */}
      {op && (
        <>
          <Button
            variant="ghost" size="sm"
            className="mt-3 h-7 text-xs text-muted-foreground hover:text-foreground -ml-2"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? <ChevronUp className="w-3 h-3 mr-1" /> : <ChevronDown className="w-3 h-3 mr-1" />}
            Dettagli tecnici
          </Button>
          {expanded && (
            <div className="mt-2 pt-3 border-t border-border/40 text-xs space-y-1.5 font-mono">
              <DetailRow label="Op ID" value={`#${op.operation_id}`} />
              <DetailRow label="Stato DB" value={`${op.zanshin_state} (shield=${op.shield_phase})`} />
              <DetailRow label="Direzione" value={op.prop_direction || '—'} />
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 pt-1">
                <DetailRow label="PROP entry"  value={fmtPrice(op.prop_entry_price)} />
                <DetailRow label="BRO  entry"  value={fmtPrice(op.broker_entry_price)} />
                <DetailRow label="PROP SL"     value={fmtPrice(op.prop_sl_current)} />
                <DetailRow label="BRO  SL"     value={fmtPrice(op.broker_sl_current)} />
                <DetailRow label="PROP exp.SL" value={fmtPrice(op.expected_prop_sl)} />
                <DetailRow label="BRO  exp.SL" value={fmtPrice(op.expected_broker_sl)} />
                <DetailRow label="PROP TP"     value={fmtPrice(op.prop_tp_current)} />
                <DetailRow label="BRO  TP"     value={fmtPrice(op.broker_tp_current)} />
                <DetailRow label="PROP lots"   value={fmtPrice(op.prop_lots)} />
                <DetailRow label="BRO  lots"   value={fmtPrice(op.broker_lots)} />
              </div>
              {op.last_decision_ts && (
                <div className="pt-2 border-t border-border/30">
                  <div className="text-muted-foreground">Ultima decisione:</div>
                  <div className="text-foreground">
                    {op.last_decision_event} {op.last_decision_action ? `· ${op.last_decision_action}` : ''}
                  </div>
                  {op.last_decision_reason && (
                    <div className="text-muted-foreground break-words mt-0.5">
                      {op.last_decision_reason}
                    </div>
                  )}
                  <div className="text-[10px] text-muted-foreground mt-1">
                    {new Date(op.last_decision_ts).toLocaleString('it-IT')}
                  </div>
                </div>
              )}
              {op.stale_cycles > 0 && (
                <div className="pt-2 text-orange-400">
                  ⚠ Stale cycles: {op.stale_cycles}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </Card>
  )
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-muted-foreground">{label}:</span>
      <span className="text-foreground tabular-nums">{value}</span>
    </div>
  )
}
